import React, { useEffect } from "react";
import { IoMdSettings } from "react-icons/io";
import { IoDocumentText } from "react-icons/io5";
import { MdOutlineCode, MdPictureAsPdf, MdUndo } from "react-icons/md";

import { PaymentApi } from "api";
import { paymentsApi } from "api/payments/paymentsApi";
import { useCoursesOptions } from "api/hooks";
import { usePayments } from "api/hooks/usePayments";
import { useUsersOptions } from "api/hooks/useUsers";
import { useAutopayRefundsEnabled } from "api/hooks/useAutopayRefundsEnabled";
import { PageMain } from "components/molecules";
import { PageRichHeader } from "components/molecules/PageHeader/PageRichHeader";
import { routes } from "consts";
import { IDropdownOption, PaziaButton, PaziaLoader } from "components/atoms";
import {
  INVOICE_TYPE_LABELS,
  INVOICE_TYPE_OPTIONS,
  PAYMENT_STATUSES_ELEMENTS,
  PAYMENT_STATUSES_OPTIONS,
  REFUND_STATUSES,
  REFUND_STATUSES_LABELS,
} from "consts";
import { TanTable, TanTableColumn, useFilters, useInitFilters, usePagination, useSorting } from "components/molecules";
import { formatDate } from "utils";
import { Link, useNavigate } from "react-router-dom";
import { formatPln, RefundPaymentModal } from "./RefundPaymentModal";
import { RegenerateInvoiceModal } from "./RegenerateInvoiceModal";
import { GenerateCustomInvoiceModal } from "./GenerateCustomInvoiceModal";
import { KsefXmlModal } from "./KsefXmlModal";
import { getApiErrorMessage, useToaster } from "utils";
import {
  getRefundAvailability,
  REFUNDS_DISABLED_REASON,
  sumAcceptedRefunds,
  sumRequestingRefunds,
} from "./refundAvailability";

interface KsefInvoice {
  kSeFSubmitted: boolean;
  kSeFNumber: string | null;
  kSeFAcceptedAt: string | null;
}

const KsefStatusBadge = ({ invoice }: { invoice: KsefInvoice }) => {
  if (invoice.kSeFNumber) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-green-100 px-1.5 py-0.5 text-xs text-green-800">
        <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
        Wysłano
      </span>
    );
  }
  if (invoice.kSeFSubmitted) {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-yellow-100 px-1.5 py-0.5 text-xs text-yellow-800">
        <span className="h-1.5 w-1.5 rounded-full bg-yellow-500" />
        Wysyłanie
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500">
      <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
      Nie wysłano
    </span>
  );
};

const openKsefInvoicePdfInNewTab = async (paymentId: number, courseId?: number) => {
  const blob = await paymentsApi.downloadKsefInvoicePdf(paymentId, courseId);
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
  // URL revoke po 60s — przeglądarka zdąży załadować PDF
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
};

const FILTER_NAME = "finances-filter";
export const FinancesPage = () => {
  const { getInitFilterValue, getInitFilterValues } = useInitFilters(FILTER_NAME);
  const { isLoading, options } = useUsersOptions();
  const { isLoading: loadingCourses, options: coursesOptions } = useCoursesOptions();

  if (isLoading || loadingCourses) return <PaziaLoader position="center" />;

  return (
    <FinancesPageInner
      initValues={{
        id: getInitFilterValue("id", "like") ?? "",
        status: getInitFilterValues("status"),
      }}
      usersOptions={options}
      coursesOptions={coursesOptions}
    />
  );
};

interface FinancesPageInnerProps {
  initValues: {
    id: string;
    status: number[];
  };
  usersOptions: IDropdownOption[];
  coursesOptions: IDropdownOption[];
}
export const FinancesPageInner = ({ initValues, usersOptions, coursesOptions }: FinancesPageInnerProps) => {
  const navigate = useNavigate();
  const { searchPhrase, filter, updateAllFilters } = useFilters(FILTER_NAME);
  const { showError, showSuccess, showWarning } = useToaster();
  const refundsEnabled = useAutopayRefundsEnabled();
  const [refundModal, setRefundModal] = React.useState<PaymentApi | null>(null);
  const [retryingRefundId, setRetryingRefundId] = React.useState<number | null>(null);
  const [generateInvoiceModal, setGenerateInvoiceModal] = React.useState<PaymentApi | null>(null);
  const [customInvoiceModal, setCustomInvoiceModal] = React.useState<{ payment: PaymentApi; courseId?: number } | null>(
    null,
  );
  const [ksefXmlModal, setKsefXmlModal] = React.useState<{ payment: PaymentApi; courseId?: number } | null>(null);
  const { limit, onPaginationChange, skip, pagination } = usePagination();
  const { sorting, onSortingChange, fieldWithOrder } = useSorting("createdDate", "DESC");

  const { isFetching, isLoading, invalidate, payments, total } = usePayments(
    skip,
    limit,
    searchPhrase,
    filter,
    fieldWithOrder,
  );

  useEffect(() => {
    invalidate();
  }, [sorting, pagination, searchPhrase, filter]);

  const retryRefund = async (refundId: number) => {
    if (retryingRefundId != null) return;
    setRetryingRefundId(refundId);
    try {
      const { data } = await paymentsApi.retryRefund(refundId);
      if (data.status === REFUND_STATUSES.Rejected) showError(data.error ?? "Autopay odrzucił zwrot");
      else if (data.status === REFUND_STATUSES.Requesting) {
        showWarning(data.error ?? "Zwrot nadal czeka na odpowiedź Autopay");
      } else showSuccess("Zwrot zlecony");
    } catch (error) {
      showError(getApiErrorMessage(error, "Wystąpił błąd podczas ponawiania zwrotu"));
    } finally {
      setRetryingRefundId(null);
      invalidate();
    }
  };

  const columns: TanTableColumn<PaymentApi>[] = [
    {
      id: "id",
      accessorKey: "id",
      size: 10,
      header: "ID",
      enableColumnFilter: true,
      enableSorting: true,
      meta: {
        filterVariant: "id",
      },
    },
    {
      id: "user",
      accessorKey: "user",
      header: "Użytkownik",
      enableColumnFilter: true,
      enableSorting: true,
      meta: {
        filterVariant: "select",
        options: usersOptions,
      },
      cell: (row) => {
        if (!row.row.original.user) return <span>Usunięty użytkownik</span>;
        const fullName = row.row.original.user.firstName + " " + row.row.original.user.surname;

        // For multi-course payments, link without courseId
        if (row.row.original.isMultiCourse) {
          return (
            <Link target="_blank" to={`/student/${row.row.original.user.id}/payments`} className="hover:underline">
              {fullName}
            </Link>
          );
        }

        return (
          <Link
            target="_blank"
            to={routes.STUDENT_NESTED.PAYMENTS(row.row.original.user.id, row.row.original.courseId!)}
            className="hover:underline"
          >
            {fullName}
          </Link>
        );
      },
    },
    {
      id: "course",
      accessorKey: "course",
      header: "Kurs",
      enableColumnFilter: true,
      enableSorting: true,
      meta: {
        filterVariant: "select",
        options: coursesOptions,
      },
      cell: (row) => {
        if (row.row.original.isMultiCourse) {
          return (
            <div className="flex flex-col">
              <span className="font-semibold text-primary-700">Płatność multi-kursowa</span>
              <span className="text-xs text-slate-500">Kursy: {row.row.original.courses?.length || 0}</span>
            </div>
          );
        }
        const payment = row.row.original;
        if (payment.refundOfId != null) {
          const label = `#${payment.refundOfId}`;
          return (
            <div className="flex flex-col">
              <span>{payment.courseName}</span>
              <span className="text-xs text-slate-500">
                do płatności{" "}
                {payment.user && payment.courseId ? (
                  <Link
                    target="_blank"
                    to={routes.STUDENT_NESTED.PAYMENTS(payment.user.id, payment.courseId)}
                    className="hover:underline"
                  >
                    {label}
                  </Link>
                ) : (
                  label
                )}
              </span>
            </div>
          );
        }
        return <span>{payment.courseName}</span>;
      },
    },
    // { // TODO Uncomment jak egzaminy
    //   id: "userExamName",
    //   accessorKey: "userExamName",
    //   header: "Egzamin",
    //   enableColumnFilter: false,
    //   enableSorting: false,
    // },
    {
      id: "status",
      accessorKey: "status",
      header: "Status",
      cell: (row) => {
        const payment = row.row.original;
        if (payment.refundOfId != null) {
          return (
            <div>
              <span className="mr-1 rounded bg-purple-100 px-1.5 py-0.5 text-xs text-purple-700">Zwrot</span>
              <span>{payment.refundStatus != null ? REFUND_STATUSES_LABELS[payment.refundStatus] : ""}</span>
              {payment.refundError && <div className="text-xs text-red-500">{payment.refundError}</div>}
            </div>
          );
        }
        return (
          <div>
            <span>{PAYMENT_STATUSES_ELEMENTS[row.row.original.status]}</span>
            <br />
            {row.row.original.outsideSystem && <span className="text-xs text-primary-700">(poza systemem)</span>}
          </div>
        );
      },
      enableColumnFilter: true,
      enableSorting: true,
      meta: {
        filterVariant: "select",
        options: PAYMENT_STATUSES_OPTIONS,
      },
    },
    {
      id: "amount",
      accessorKey: "amount",
      header: "Kwota",
      enableColumnFilter: false,
      enableSorting: true,
      cell: (row) => {
        const payment = row.row.original;
        if (payment.refundOfId != null) return <span>{"-" + payment.amount + " PLN"}</span>;
        const accepted = sumAcceptedRefunds(payment);
        const requesting = sumRequestingRefunds(payment);
        return (
          <div>
            <span>{payment.amount + " PLN"}</span>
            {(accepted > 0 || requesting > 0) && (
              <div className="text-xs text-slate-500">
                {accepted > 0 && <>zwrócono {formatPln(accepted)} zł</>}
                {accepted > 0 && requesting > 0 && " "}
                {requesting > 0 && <>(w trakcie: {formatPln(requesting)} zł)</>}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: "createdDate",
      accessorKey: "createdDate",
      header: "Data",
      enableColumnFilter: true,
      enableSorting: true,
      cell: (row) => formatDate(row.row.original.createdDate, true, true),
      meta: {
        filterVariant: "date-range",
      },
    },
    {
      id: "orderID",
      accessorKey: "orderID",
      header: "ID Zamówienia",
      enableColumnFilter: true,
    },
    {
      id: "invoiceType",
      accessorKey: "id",
      size: 10,
      header: "Typ faktury",
      enableColumnFilter: true,
      enableSorting: false,
      meta: {
        filterVariant: "select",
        options: INVOICE_TYPE_OPTIONS,
      },
      cell: (row) => {
        if (row.row.original.isMultiCourse) {
          const invoiceCount = row.row.original.courses?.filter((c) => c.hasInvoice).length || 0;
          if (invoiceCount === 0) return <span className="text-slate-300">Brak</span>;

          // Get unique invoice types from courses
          const types = new Set(
            row.row.original.courses?.filter((c) => c.invoice).map((c) => INVOICE_TYPE_LABELS[c.invoice!.type]),
          );
          return <span>{Array.from(types).join(", ")}</span>;
        }

        if (!row.row.original.invoice) return <span className="text-slate-300">Brak</span>;
        return <span>{INVOICE_TYPE_LABELS[row.row.original.invoice.type]}</span>;
      },
    },
    {
      id: "invoiceNumber",
      accessorKey: "id",
      size: 10,
      header: "Numer faktury",
      enableColumnFilter: true,
      enableSorting: false,
      cell: (row) => {
        if (row.row.original.isMultiCourse) {
          const invoices = row.row.original.courses?.filter((c) => c.invoice).map((c) => c.invoice!);
          if (!invoices || invoices.length === 0) return <span className="text-slate-300">Brak</span>;

          return (
            <div className="flex flex-col text-xs">
              {invoices.map((inv, idx) => (
                <span key={idx}>
                  {inv.number}/{inv.month}/{inv.year}
                </span>
              ))}
            </div>
          );
        }

        if (!row.row.original.invoice) return <span className="text-slate-300">Brak</span>;
        return (
          <span>
            {row.row.original.invoice.number}/{row.row.original.invoice.month}/{row.row.original.invoice.year}
          </span>
        );
      },
    },
    {
      id: "ksefStatus",
      accessorKey: "id",
      header: "KSeF",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (row) => {
        if (row.row.original.isMultiCourse) {
          const invoices = row.row.original.courses?.filter((c) => c.invoice).map((c) => c.invoice!);
          if (!invoices || invoices.length === 0) return <span className="text-slate-300">–</span>;
          return (
            <div className="flex flex-col gap-1 text-xs">
              {invoices.map((inv, idx) => (
                <KsefStatusBadge key={idx} invoice={inv} />
              ))}
            </div>
          );
        }
        if (!row.row.original.invoice) return <span className="text-slate-300">–</span>;
        return <KsefStatusBadge invoice={row.row.original.invoice} />;
      },
    },
    {
      id: "comment",
      accessorKey: "comment",
      header: "Komentarz",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (row) => {
        const payment = row.row.original;
        if (payment.refundOfId != null && payment.comment) return <span>Notatka: {payment.comment}</span>;
        return <span>{payment.comment}</span>;
      },
    },
    {
      id: "refundActions",
      accessorKey: "id",
      size: 10,
      header: "Zwrot",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (row) => {
        const payment = row.row.original;
        if (payment.refundOfId != null) {
          if (payment.refundStatus !== REFUND_STATUSES.Requesting) return null;
          return (
            <span title={refundsEnabled ? undefined : REFUNDS_DISABLED_REASON}>
            <PaziaButton
              type="tertiary"
              btnType="button"
              disabled={retryingRefundId != null || !refundsEnabled}
              processing={retryingRefundId === payment.id}
              onClick={() => retryRefund(payment.id)}
            >
              Ponów
            </PaziaButton>
            </span>
          );
        }
        const availability = getRefundAvailability(payment, refundsEnabled);
        if (!availability.canRefund) {
          return <MdUndo title={availability.reason} className="text-slate-300" size={20} />;
        }
        return (
          <MdUndo
            title="Zwróć"
            onClick={() => setRefundModal(payment)}
            className="cursor-pointer hover:text-primary-700"
            size={20}
          />
        );
      },
    },
    {
      id: "invoiceActions",
      accessorKey: "id",
      size: 10,
      header: "Faktura",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (row) => {
        if (row.row.original.refundOfId != null) return null;
        if (row.row.original.isMultiCourse) {
          return (
            <div className="flex items-center gap-2">
              <div className="flex flex-col gap-1">
                {row.row.original.courses?.map((course, idx) => (
                  <IoDocumentText
                    key={idx}
                    title={`Faktura niestandardowa - ${course.courseName}`}
                    onClick={() => {
                      if (row.row.original.status === 2) {
                        setCustomInvoiceModal({ payment: row.row.original, courseId: course.courseId });
                      }
                    }}
                    className={`${row.row.original.status == 2 ? "cursor-pointer" : "text-slate-300"}`}
                    size={20}
                  />
                ))}
              </div>
              <div className="flex flex-col gap-1">
                {row.row.original.courses?.map((course, idx) => (
                  <MdOutlineCode
                    key={idx}
                    title={`XML KSeF - ${course.courseName}`}
                    onClick={() => setKsefXmlModal({ payment: row.row.original, courseId: course.courseId })}
                    className="cursor-pointer hover:text-primary-700"
                    size={20}
                  />
                ))}
              </div>
              <div className="flex flex-col gap-1">
                {row.row.original.courses?.map((course, idx) => (
                  <MdPictureAsPdf
                    key={`pdf-${idx}`}
                    title={`Faktura KSeF (PDF) - ${course.courseName}`}
                    onClick={() =>
                      course.invoice?.kSeFNumber
                        ? openKsefInvoicePdfInNewTab(row.row.original.id, course.courseId)
                        : undefined
                    }
                    className={course.invoice?.kSeFNumber ? "cursor-pointer hover:text-primary-700" : "text-slate-300"}
                    size={20}
                  />
                ))}
              </div>
            </div>
          );
        }

        return (
          <div className="flex gap-2">
            {/* <IoMdSettings
              title="Generuj fakturę"
              onClick={() => {
                if (row.row.original.status === 2) {
                  setGenerateInvoiceModal(row.row.original);
                }
              }}
              className={`${row.row.original.status == 2 ? "cursor-pointer" : "text-slate-300"}`}
              size={20}
            /> */}
            {/* <IoDocumentText
              title="Faktura niestandardowa"
              onClick={() => {
                if (row.row.original.status === 2) {
                  setCustomInvoiceModal({ payment: row.row.original, courseId: undefined });
                }
              }}
              className={`${row.row.original.status == 2 ? "cursor-pointer" : "text-slate-300"}`}
              size={20}
            /> */}
            <MdOutlineCode
              title="XML KSeF"
              onClick={() => setKsefXmlModal({ payment: row.row.original, courseId: undefined })}
              className="cursor-pointer hover:text-primary-700"
              size={20}
            />
            <MdPictureAsPdf
              title="Faktura KSeF (PDF)"
              onClick={() =>
                row.row.original.invoice?.kSeFNumber
                  ? openKsefInvoicePdfInNewTab(row.row.original.id, undefined)
                  : undefined
              }
              className={
                row.row.original.invoice?.kSeFNumber ? "cursor-pointer hover:text-primary-700" : "text-slate-300"
              }
              size={20}
            />
          </div>
        );
      },
    },
  ];
  return (
    <PageMain>
      <PageRichHeader header="Finanse">
        <div className="flex grow justify-end gap-2">
          <div>
            <PaziaButton
              type="tertiary"
              title="Opóźnione płatności"
              onClick={() => navigate(routes.DELAYED_PAYMENTS)}
            />
          </div>
          <div>
            <PaziaButton
              type="tertiary"
              title="Edytuj kategorie płatności"
              onClick={() => navigate(routes.COURSE_FINANCES_CATEGORIES)}
            />
          </div>
        </div>
      </PageRichHeader>

      <TanTable
        data={payments.map((x) => ({ ...x, course: x.courseName }))}
        columns={columns}
        total={total}
        loading={isLoading}
        fetching={isFetching}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        pagination={pagination}
        sorting={sorting}
        onClearFilters={() => updateAllFilters([])}
        onFiltersChange={(filters) => {
          updateAllFilters(filters.map((x) => ({ key: x.id, value: x.apiFilter })));
        }}
        initialFilters={[
          {
            id: "id",
            value: initValues.id,
          },
          {
            id: "status",
            value: initValues.status,
          },
        ]}
      />
      <RefundPaymentModal
        refund={refundModal}
        refundable={refundModal ? (() => {
          const a = getRefundAvailability(refundModal, refundsEnabled);
          return a.canRefund ? a.refundable : 0;
        })() : 0}
        onCloseRequest={() => setRefundModal(null)}
        onRefunded={invalidate}
      />
      <RegenerateInvoiceModal
        payment={generateInvoiceModal}
        onCloseRequest={(generated) => {
          setGenerateInvoiceModal(null);
          invalidate();
        }}
      />
      <GenerateCustomInvoiceModal
        payment={customInvoiceModal?.payment ?? null}
        courseId={customInvoiceModal?.courseId}
        onCloseRequest={(generated) => {
          setCustomInvoiceModal(null);
          if (generated) {
            invalidate();
          }
        }}
      />
      <KsefXmlModal
        payment={ksefXmlModal?.payment ?? null}
        courseId={ksefXmlModal?.courseId}
        onCloseRequest={() => setKsefXmlModal(null)}
      />
    </PageMain>
  );
};
